import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  Flex,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { RepaymentEntry, Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { LineChartWithFormatters } from 'packages/common-frontend/components/LineChartWithFormatters';
import { BasicTable } from 'packages/common-frontend/components/TanStack';
import { useGetRepaymentDataForAssignedReceivableQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import {
  ChartTimePeriod,
  dateFormatter,
  formatDataByTimePeriod,
} from 'packages/portfolio/ui/shared/portfolioChartUtils';

type RepaymentTableRow = {
  repaymentDate: Util.FullDateStr;
  amountPaid: number;
  totalRepaid: number;
};

const formatRepaymentRecords = (
  records: RepaymentEntry.ReceivableRepaymentsHistory[],
  selectedTimePeriod: ChartTimePeriod,
) => {
  const orderedChartData: {
    finleyRepaymentDate: Util.FullDateStr;
    [key: string]: Util.Primitive;
  }[] = _.orderBy(records, ['finleyRepaymentDate'], ['asc']);

  return formatDataByTimePeriod({
    chartData: orderedChartData,
    selectedTimePeriod,
    dateFieldName: 'finleyRepaymentDate',
  });
};

type RepaymentHistoryTableProps = {
  repaymentData: {
    [key: string]: Util.Primitive;
    finleyRepaymentDate: Util.FullDateStr;
  }[];
};
const RepaymentHistoryTable = ({
  repaymentData,
}: RepaymentHistoryTableProps) => {
  const [tableData, setTableData] = useState<RepaymentTableRow[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    let previousBalance = 0;
    const repaymentTableData: RepaymentTableRow[] = repaymentData.map(
      (repayment) => {
        const repaymentAmount = Number(repayment.normalizedRepaymentAmount);
        const totalRepaid = repaymentAmount + previousBalance;
        previousBalance = totalRepaid;
        return {
          repaymentDate: repayment.finleyRepaymentDate,
          amountPaid: repaymentAmount,
          totalRepaid,
        };
      },
    );
    setTableData(repaymentTableData);
  }, [repaymentData]);

  const columns: ColumnDef<any, any>[] = [
    {
      id: 'repaymentDate',
      accessorKey: 'repaymentDate',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Repayment Date
          </Text>
        );
      },
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
    },
    {
      id: 'amountPaid',
      accessorKey: 'amountPaid',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Amount Paid
          </Text>
        );
      },
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
    },
    {
      id: 'totalRepaid',
      accessorKey: 'totalRepaid',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Total Repaid
          </Text>
        );
      },
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
      enableSorting: false,
    },
  ];

  const table = useReactTable({
    data: tableData.slice().reverse(),
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <BasicTable table={table} />;
};

export const GraphTypeIcon = ({
  graphType,
  selectedGraphType,
  setSelectedGraphType,
}: {
  graphType: 'percent' | 'dollar';
  selectedGraphType: 'percent' | 'dollar';
  setSelectedGraphType: React.Dispatch<
    React.SetStateAction<'percent' | 'dollar'>
  >;
}) => {
  const icon = graphType === 'dollar' ? 'dollar-sign' : 'percent';
  const isSelected = graphType === selectedGraphType;
  return (
    <Flex
      w="45px"
      h={9}
      justifyContent="center"
      _hover={{ bg: isSelected ? colors.aurora[800] : 'gray.50' }}
      borderRadius="8px"
      borderColor="gray.200"
      borderWidth="2px"
      color={isSelected ? 'white' : 'black'}
      bgColor={isSelected ? colors.aurora[400] : 'white'}
      cursor="pointer"
      onClick={() => setSelectedGraphType(graphType)}
    >
      <Center>
        <Icon icon={icon} size="medium" />
      </Center>
    </Flex>
  );
};

export const GraphTypeToggle = ({
  selectedGraphType,
  setSelectedGraphType,
}: {
  selectedGraphType: 'percent' | 'dollar';
  setSelectedGraphType: React.Dispatch<
    React.SetStateAction<'percent' | 'dollar'>
  >;
}) => {
  return (
    <Flex>
      <Box mr="16px">
        <GraphTypeIcon
          graphType="percent"
          selectedGraphType={selectedGraphType}
          setSelectedGraphType={setSelectedGraphType}
        />
      </Box>
      <GraphTypeIcon
        graphType="dollar"
        selectedGraphType={selectedGraphType}
        setSelectedGraphType={setSelectedGraphType}
      />
    </Flex>
  );
};

type RepaymentHistoryProps = {
  organizationId: string;
  finleyReceivableId: string;
  selectedTimePeriod: ChartTimePeriod;
  initialBalance: number | null;
};
export const RepaymentHistory = ({
  organizationId,
  finleyReceivableId,
  selectedTimePeriod,
  initialBalance,
}: RepaymentHistoryProps) => {
  const navigate = useNavigate();

  const [repaymentData, setRepaymentData] = useState<
    | {
        [key: string]: Util.Primitive;
        finleyRepaymentDate: Util.FullDateStr;
      }[]
    | null
  >(null);

  const [selectedGraphType, setSelectedGraphType] = useState<
    'percent' | 'dollar'
  >('dollar');

  const [graphData, setGraphData] = useState<
    {
      xAxisValue: any;
      [key: string]: any;
    }[]
  >([]);

  const {
    loading: repaymentDataLoading,
  } = useGetRepaymentDataForAssignedReceivableQuery({
    variables: {
      organizationId,
      finleyReceivableId,
    },
    onCompleted: (response) => {
      if (response.repaymentDataForAssignedReceivable) {
        const formattedRepaymentData = formatRepaymentRecords(
          response.repaymentDataForAssignedReceivable,
          selectedTimePeriod,
        );

        setRepaymentData(formattedRepaymentData);

        const repaymentGraphData: {
          xAxisValue: any;
          [key: string]: any;
        }[] = [];

        let previousBalance = 0;

        formattedRepaymentData.forEach((record) => {
          const repaymentAmount = record.normalizedRepaymentAmount as number;
          if (repaymentAmount !== undefined) {
            repaymentGraphData.push({
              xAxisValue: record.finleyRepaymentDate,
              repayment:
                initialBalance && selectedGraphType === 'percent'
                  ? (previousBalance + repaymentAmount) / initialBalance
                  : previousBalance + repaymentAmount,
            });
            previousBalance += repaymentAmount;
          }
        });

        setGraphData(repaymentGraphData);
      }
    },
    onError: () => {
      navigate('/');
    },
  });

  return (
    <>
      <Box
        backgroundColor={colors.background[50]}
        mt="40px"
        border="none"
        borderRadius="8px"
        padding="40px"
      >
        <Flex mb="40px" justifyContent="space-between">
          <Text fontSize="2xl" fontWeight="semibold">
            Repayment History
          </Text>
          {initialBalance && (
            <GraphTypeToggle
              selectedGraphType={selectedGraphType}
              setSelectedGraphType={setSelectedGraphType}
            />
          )}
        </Flex>
        {repaymentDataLoading && (
          <Skeleton
            data-testid="loading-receivable-repayments-history"
            height="400px"
            width="100%"
          />
        )}
        {!repaymentDataLoading && !repaymentData && (
          <Alert status="warning" borderRadius="6px">
            <AlertIcon />
            <AlertDescription fontWeight="semibold">
              Repayments process has not been set up yet. Contact your Finley
              Representative to explore additional features.
            </AlertDescription>
          </Alert>
        )}
        {repaymentData && repaymentData.length === 0 && (
          <Alert status="warning" borderRadius="6px">
            <AlertIcon />
            <AlertDescription fontWeight="semibold">
              Data is not available for the selected date range. Select another
              date range.
            </AlertDescription>
          </Alert>
        )}
        {repaymentData && repaymentData.length > 0 && (
          <Box
            padding="40px"
            background="white"
            borderRadius="8px"
            border="none"
          >
            <LineChartWithFormatters
              data={graphData}
              yAxisTickFormatterType={
                selectedGraphType === 'dollar' ? 'currency' : 'percentage'
              }
              xAxisTickFormatterType="months"
              coloredLines
              hideNodes
              xAxisTickFormatter={(value: any) =>
                dateFormatter({ date: value, timePeriod: selectedTimePeriod })
              }
              tooltip={{
                tooltipType:
                  selectedGraphType === 'dollar' ? 'currency' : 'percentage',
                tooltipLabelType: 'date',
                tooltipName:
                  selectedGraphType === 'dollar' ? 'Repayment' : 'Repayment %',
              }}
            />
            <RepaymentHistoryTable repaymentData={repaymentData} />
          </Box>
        )}
      </Box>
    </>
  );
};
